<template>
  <div class="pa-2 tabla-resultados">
    <div v-if="loading" class="pa-6 text-center grey--text">Cargando...</div>

    <v-simple-table v-else-if="data" dense fixed-header>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left col-concepto">Concepto</th>
            <th v-for="col in data.columnas" :key="'h-' + col.id" class="text-right">{{ col.name }}</th>
            <th class="text-right font-weight-bold">TOTAL</th>
          </tr>
        </thead>

        <!-- Vista en bloques: Disponibilidades / Valores / Egresos / Ingresos -->
        <tbody v-if="by_blocks">
          <template v-for="bloque in data.bloques">
            <tr :key="'b-' + bloque.id" :class="blockClass(bloque.id)">
              <td class="font-weight-bold text-uppercase col-concepto" :class="blockClass(bloque.id)">{{ bloque.name }}</td>
              <td v-for="col in data.columnas" :key="'b-' + bloque.id + '-' + col.id"></td>
              <td></td>
            </tr>

            <tr v-for="row in bloque.filas" :key="'b-' + bloque.id + '-r-' + row.concepto_id">
              <td class="col-concepto">{{ row.concepto_name }}</td>
              <td
                v-for="col in data.columnas"
                :key="'b-' + bloque.id + '-r-' + row.concepto_id + '-' + col.id"
                class="text-right"
                :class="cellClass(row.valores[col.id], bloque.id)"
                @click="onCellClick(row, col, bloque)"
              >
                {{ formatCell(row.valores[col.id]) }}
              </td>
              <td class="text-right font-weight-bold" :class="colorClass(row.total, bloque.id)">{{ formatCell(row.total) }}</td>
            </tr>

            <tr :key="'b-' + bloque.id + '-tot'" class="grey lighten-3">
              <td class="font-weight-bold col-concepto grey lighten-3">Total {{ bloque.name }}</td>
              <td v-for="col in data.columnas" :key="'b-' + bloque.id + '-tot-' + col.id" class="text-right font-weight-bold" :class="colorClass(bloque.totales[col.id], bloque.id)">
                {{ formatCell(bloque.totales[col.id]) }}
              </td>
              <td class="text-right font-weight-bold" :class="colorClass(bloque.total, bloque.id)">{{ formatCell(bloque.total) }}</td>
            </tr>

            <!-- Consolidado de los dos bloques de cuentas: cuanto se movio la caja en neto,
                 sin el ida y vuelta de los cheques en transito. Solo baja si hubo Valores. -->
            <tr v-if="data.totales.neto_despues_de === bloque.id" :key="'b-' + bloque.id + '-neto'" class="blue-grey lighten-4">
              <td class="font-weight-bold col-concepto blue-grey lighten-4">Total Neto Disponibilidades</td>
              <td v-for="col in data.columnas" :key="'b-' + bloque.id + '-neto-' + col.id" class="text-right font-weight-bold" :class="resultClass(data.totales.neto_disponibilidades[col.id])">
                {{ formatCell(data.totales.neto_disponibilidades[col.id]) }}
              </td>
              <td class="text-right font-weight-bold" :class="resultClass(data.totales.neto_disponibilidades_total)">
                {{ formatCell(data.totales.neto_disponibilidades_total) }}
              </td>
            </tr>

            <!-- Subtotal corrido de todo lo que va arriba (Disponibilidades + Valores + Egresos).
                 El backend indica detras de que bloque va; si no lo indica, la fila cierra la
                 matriz al pie, como en la vista plana. -->
            <tr v-if="data.totales.resultado_despues_de === bloque.id" :key="'b-' + bloque.id + '-res'" class="blue lighten-4">
              <td class="font-weight-bold col-concepto blue lighten-4">RESULTADO</td>
              <td v-for="col in data.columnas" :key="'b-' + bloque.id + '-res-' + col.id" class="text-right font-weight-bold" :class="resultClass(data.totales.resultado_por_centro[col.id])">
                {{ formatCell(data.totales.resultado_por_centro[col.id]) }}
              </td>
              <td class="text-right font-weight-bold" :class="resultClass(data.totales.resultado_total)">{{ formatCell(data.totales.resultado_total) }}</td>
            </tr>
          </template>

          <tr v-if="!data.totales.resultado_despues_de" class="blue lighten-4">
            <td class="font-weight-bold col-concepto blue lighten-4">RESULTADO</td>
            <td v-for="col in data.columnas" :key="'res-' + col.id" class="text-right font-weight-bold" :class="resultClass(data.totales.resultado_por_centro[col.id])">
              {{ formatCell(data.totales.resultado_por_centro[col.id]) }}
            </td>
            <td class="text-right font-weight-bold" :class="resultClass(data.totales.resultado_total)">
              {{ formatCell(data.totales.resultado_total) }}
            </td>
          </tr>

          <!-- Lo acumulado hasta RESULTADO (Disponibilidades + Valores + Egresos) mas los
               Ingresos. Con toda la matriz en el signo del movimiento son dos vistas del mismo
               dinero, asi que cierra en cero por construccion: distinto de cero es una
               inconsistencia real (movimientos asentados al reves, por ejemplo), no un dato
               informativo. Los dos bloques de cuentas van juntos porque un transito de cheque
               mueve fondos contra valores: por separado ninguno netea cero. Las reclasificaciones
               NO lo mueven: netean cero. -->
          <tr v-if="data.totales.control" class="grey lighten-4">
            <td class="font-weight-bold col-concepto grey lighten-4">
              CONTROL
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                </template>
                <span>RESULTADO más Ingresos, es decir Disponibilidades, Valores en tránsito, Egresos e Ingresos sumados. Debe dar cero: son dos vistas del mismo dinero. Distinto de cero indica una inconsistencia a revisar, por ejemplo movimientos asentados con el sentido invertido.</span>
              </v-tooltip>
            </td>
            <td v-for="col in data.columnas" :key="'ctrl-' + col.id" class="text-right font-weight-bold">
              {{ formatCell(data.totales.control[col.id]) }}
            </td>
            <td class="text-right font-weight-bold">
              {{ formatCell(data.totales.control_total) }}
            </td>
          </tr>
        </tbody>

        <!-- Vista plana: una fila por concepto -->
        <tbody v-else>
          <tr v-for="row in data.conceptos" :key="'con-' + row.concepto_id">
            <td class="col-concepto">{{ row.concepto_name }}</td>
            <td
              v-for="col in data.columnas"
              :key="'con-' + row.concepto_id + '-' + col.id"
              class="text-right"
              :class="cellClass(row.valores[col.id])"
              @click="onCellClick(row, col)"
            >
              {{ formatCell(row.valores[col.id]) }}
            </td>
            <td class="text-right font-weight-bold">{{ formatCell(row.total) }}</td>
          </tr>

          <!-- RESULTADO -->
          <tr class="blue lighten-4">
            <td class="font-weight-bold col-concepto blue lighten-4">RESULTADO</td>
            <td v-for="col in data.columnas" :key="'res-' + col.id" class="text-right font-weight-bold" :class="resultClass(data.totales.resultado_por_centro[col.id])">
              {{ formatCell(data.totales.resultado_por_centro[col.id]) }}
            </td>
            <td class="text-right font-weight-bold" :class="resultClass(data.totales.resultado_total)">
              {{ formatCell(data.totales.resultado_total) }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <div v-else class="pa-6 text-center grey--text">{{ empty_text }}</div>
  </div>
</template>

<script>
import { formatUtils } from "@/libs/utils/formatUtils";

export default {
  props: {
    data: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    empty_text: {
      type: String,
      default: "Seleccioná un período y presioná Buscar.",
    },
    // Cuando la respuesta trae bloques (Disponibilidades / Ingresos / Egresos) los separa
    // visualmente, con subtotal por bloque. Si no, una unica lista de conceptos.
    by_blocks: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      block_classes: {
        cuenta: "blue-grey lighten-4",
        // Valores en transito es la contrapartida de Disponibilidades: mismo tono, mas suave.
        valor: "blue-grey lighten-5",
        ingreso: "green lighten-4",
        egreso: "red lighten-4",
      },
      // Bloques donde un importe negativo se pinta en rojo por ser una perdida de fondos.
      color_by_sign_blocks: ["cuenta"],
    };
  },
  methods: {
    // La matriz es mayormente ceros (cuentas sin movimiento ese dia, conceptos de otro mes).
    // Se marcan con un guion: deja ver de un vistazo donde hubo movimiento real, y a la vez
    // distingue la celda calculada en cero de una que no corresponde.
    formatCell(value) {
      if (!this.hasValue(value)) return "-";
      return formatUtils.formatPrice(value);
    },
    blockClass(id) {
      return this.block_classes[id] || "grey lighten-3";
    },
    hasValue(value) {
      return Math.abs(parseFloat(value) || 0) > 0;
    },
    // La matriz va en negro: el signo lo trae el importe. La excepcion son las filas donde un
    // negativo es una perdida y hay que verla de lejos: Disponibilidades y la fila RESULTADO.
    colorClass(value, bloque_id) {
      if (!this.color_by_sign_blocks.includes(bloque_id)) return "";
      return this.resultClass(value);
    },
    resultClass(value) {
      return parseFloat(value) < 0 ? "red--text" : "";
    },
    cellClass(value, bloque_id) {
      return [this.colorClass(value, bloque_id), this.hasValue(value) ? "cursor" : ""];
    },
    onCellClick(row, col, bloque = null) {
      if (!this.hasValue(row.valores[col.id])) return;
      this.$emit("cell-click", {
        concepto_id: row.concepto_id,
        concepto_name: row.concepto_name,
        columna_id: col.id,
        columna_name: col.name,
        tipo_fila: bloque != null ? bloque.tipo_fila : "concepto",
        value: row.valores[col.id],
      });
    },
  },
};
</script>

<style scoped>
/* El encabezado lo fija la prop fixed-header de la tabla, que ya trae el fondo y el box-shadow que
   reemplaza al borde inferior. Lo unico que le falta es un tope de altura: sin el, el wrapper crece
   con el contenido, el scroll vertical termina siendo el de la pagina y el encabezado se va con
   ella. El descuento deja lugar para la barra fija, las solapas, los filtros y el aviso de arriba. */
.tabla-resultados >>> .v-data-table__wrapper {
  max-height: calc(100vh - 340px);
  min-height: 300px;
}
.tabla-resultados >>> th,
.tabla-resultados >>> td {
  white-space: nowrap;
}
/* El scroll horizontal deja siempre a la vista de que concepto es cada fila. */
.tabla-resultados >>> .col-concepto {
  position: sticky;
  left: 0;
  z-index: 1;
  background-color: white;
}
/* La esquina queda pegada por arriba y por izquierda: tiene que pintar sobre el resto del
   encabezado, que Vuetify deja en z-index 2. */
.tabla-resultados >>> thead th.col-concepto {
  z-index: 3;
}
</style>
